import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { GoalTemplate } from "@/apps/goals/types";
import Button from "@/ui/Button/Button";
import { useServiceT } from "@/utils/hooks/useServiceT";
import cls from "./styles.module.scss";

const CENTER = 200;
const RADIUS = 188;
const LABEL_RADIUS = 136;
const SPIN_DURATION_MS = 4200;
const FULL_SPINS = 6;

const polarToCartesian = (angle: number, radius: number) => {
  const radians = ((angle - 90) * Math.PI) / 180;

  return {
    x: CENTER + radius * Math.cos(radians),
    y: CENTER + radius * Math.sin(radians),
  };
};

const createSectorPath = (startAngle: number, endAngle: number) => {
  const start = polarToCartesian(startAngle, RADIUS);
  const end = polarToCartesian(endAngle, RADIUS);
  const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

  return [
    `M ${CENTER} ${CENTER}`,
    `L ${start.x} ${start.y}`,
    `A ${RADIUS} ${RADIUS} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`,
    "Z",
  ].join(" ");
};

interface WheelProps {
  templates: GoalTemplate[];
  onSpin: () => Promise<GoalTemplate>;
  onSpinComplete?: (template: GoalTemplate) => void;
}

export const Wheel = ({ templates, onSpin, onSpinComplete }: WheelProps) => {
  const { t } = useTranslation();
  const serviceT = useServiceT();
  const activeTemplates = templates.filter((template) => template.is_active);

  const timeoutRef = useRef<number>(null);
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [hasSpun, setHasSpun] = useState(false);
  const [selectedSector, setSelectedSector] = useState<number | null>(null);
  const sectorAngle =
    activeTemplates.length > 0 ? 360 / activeTemplates.length : 0;

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  const spin = async () => {
    if (spinning || activeTemplates.length === 0) return;

    setSpinning(true);
    setHasSpun(true);
    setSelectedSector(null);

    let winningTemplate: GoalTemplate;

    try {
      winningTemplate = await onSpin();
    } catch {
      setSpinning(false);
      setHasSpun(false);
      return;
    }

    const winningSector = Math.max(
      activeTemplates.findIndex(
        (template) => template.template_id === winningTemplate.template_id
      ),
      0
    );
    const sectorMidAngle = winningSector * sectorAngle + sectorAngle / 2;
    const targetRotation = (360 - sectorMidAngle) % 360;
    const currentRotation = ((rotation % 360) + 360) % 360;
    const alignmentDelta = (targetRotation - currentRotation + 360) % 360;
    const finalRotation = rotation + FULL_SPINS * 360 + alignmentDelta;

    setRotation(finalRotation);

    timeoutRef.current = window.setTimeout(() => {
      setSelectedSector(winningSector);
      setSpinning(false);
      onSpinComplete?.(winningTemplate);
    }, SPIN_DURATION_MS);
  };

  return (
    <div className={cls.root}>
      <div className={cls.wheelWrap}>
        <div
          className={cls.wheel}
          aria-label={serviceT("goals.wheel.ariaLabel")}
        >
          <svg className={cls.svg} viewBox="0 0 400 400" role="img">
            <defs>
              <filter id="sectorInnerShadow">
                <feOffset dx="0" dy="8" />
                <feGaussianBlur stdDeviation="10" result="offsetBlur" />
                <feComposite
                  in="offsetBlur"
                  in2="SourceAlpha"
                  operator="arithmetic"
                  k2="-1"
                  k3="1"
                  result="innerShadow"
                />
                <feColorMatrix
                  in="innerShadow"
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.48 0"
                />
                <feComposite in2="SourceGraphic" operator="over" />
              </filter>
              <filter
                id="selectedSectorGlow"
                x="-20%"
                y="-20%"
                width="140%"
                height="140%"
              >
                <feGaussianBlur stdDeviation="4" result="blur" />
                <feColorMatrix
                  in="blur"
                  type="matrix"
                  values="0 0 0 0 0.77 0 0 0 0 0.52 0 0 0 0 0.08 0 0 0 0.48 0"
                />
                <feMerge>
                  <feMergeNode />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <g
              className={[cls.sectors, spinning ? cls.spinning : ""].join(" ")}
              style={{ transform: `rotate(${rotation}deg)` }}
            >
              {activeTemplates.map((template, index) => {
                const startAngle = index * sectorAngle;
                const endAngle = startAngle + sectorAngle;
                const midAngle = startAngle + sectorAngle / 2;
                const labelPoint = polarToCartesian(midAngle, LABEL_RADIUS);
                const isSelected = selectedSector === index;
                const ridesRequired = template.rides_required;

                return (
                  <g key={template.template_id}>
                    <path
                      className={[
                        index % 2 === 0 ? cls.greenSector : cls.redSector,
                        isSelected ? cls.selectedSector : "",
                      ].join(" ")}
                      d={createSectorPath(startAngle, endAngle)}
                      filter={
                        isSelected
                          ? "url(#selectedSectorGlow)"
                          : "url(#sectorInnerShadow)"
                      }
                    />
                    <text
                      className={cls.label}
                      x={labelPoint.x}
                      y={labelPoint.y}
                      transform={`rotate(${midAngle} ${labelPoint.x} ${labelPoint.y})`}
                    >
                      <tspan className={cls.labelNumber} x={labelPoint.x} dy="">
                        {ridesRequired}
                      </tspan>
                      <tspan
                        className={cls.labelCaption}
                        x={labelPoint.x}
                        dy="1.25em"
                      >
                        {serviceT("goals.common.rides", {
                          count: ridesRequired,
                        })}
                      </tspan>
                    </text>
                  </g>
                );
              })}
            </g>
            <image
              className={cls.centerPin}
              href="/goals/wheelCenter.webp"
              x="138"
              y="138"
              width="124"
              height="124"
              preserveAspectRatio="xMidYMid meet"
            />
          </svg>
        </div>
      </div>
      <div
        className={[
          cls.spinButtonSlot,
          hasSpun ? cls.spinButtonSlotHidden : "",
        ].join(" ")}
      >
        <Button
          className={cls.spinButton}
          label={t("goals.wheel.spin")}
          disabled={spinning || activeTemplates.length === 0}
          onClick={spin}
        />
      </div>
    </div>
  );
};
